import { Component, OnInit, inject, signal } from '@angular/core';
import { PrintOfficeService } from '../../../services/print-office.service';
import { AuthService } from '../../../services/auth/auth.service';
import { GetPrintOfficeResponseDto } from '../../../models/server-models/reseponse-dtos/get-print-office-response-dto';
import { ErrorService } from '../../../../../shared/services/error.service';
import { PrintOrderDto } from '../../../models/server-models/entities/print-order-dto';
import { PrintOrderService } from '../../../services/print-order.service';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { SpActionButton, SpColumn } from '../../../models/client-models/entities/sp-columns';
import { PrintOrderFileDto } from '../../../models/server-models/entities/print-order-file-dto';
import { PrintOrderStatus } from '../../../models/server-models/enums/print-order-status';
import { FileContentType } from '../../../models/server-models/enums/file-content-type';
import { AlertController, LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { downloadFile } from '../../../../../shared/utilities';
import { BlobFilesService } from '../../../services/blob-files.service';
import { PaymentStatus } from '../../../models/server-models/enums/payment-status';

@Component({
  selector: 'app-print-order-details',
  templateUrl: './print-order-details.page.html',
  styleUrls: ['./print-order-details.page.scss'],
  standalone: false,
})
export class PrintOrderDetailsPage implements OnInit {
  printOfficeService = inject(PrintOfficeService);
  authService = inject(AuthService);
  errorService = inject(ErrorService);
  printOrderService = inject(PrintOrderService);
  blobFilesService = inject(BlobFilesService);
  activatedRoute = inject(ActivatedRoute);
  alertController = inject(AlertController);
  translateService = inject(TranslateService);
  loadingCtrl = inject(LoadingController);

  loading: HTMLIonLoadingElement | null = null;

  printOffice: GetPrintOfficeResponseDto = { printOffice: null };

  printOrderId = signal<string | null>(null);
  printOrder = signal<PrintOrderDto | null>(null);

  fileColumns: SpColumn<PrintOrderFileDto>[] = [];
  fileActions: SpActionButton<PrintOrderFileDto>[] = [];

  previousStatus = signal<PrintOrderStatus | null>(null);
  selectedStatus = signal<PrintOrderStatus | null>(null);
  printOrderStatuses = [
    PrintOrderStatus.Created,
    PrintOrderStatus.InProgress,
    PrintOrderStatus.Completed,
    PrintOrderStatus.Canceled,
    PrintOrderStatus.Collected,
  ];

  paymentStatuses = PaymentStatus;

  downloadZipMb = signal<number>(0);

  selectCompomnentOkText = signal<string>('');
  selectCompomnentCancelText = signal<string>('');

  constructor() {}

  ngOnInit() {
    this.printOrderId.set(this.activatedRoute.snapshot.paramMap.get('id'));

    this.setupTable();
    this.loadData();

    this.setSelectComponentText();
  }

  loadData() {
    this.printOrderService.getOrderForPrintOffice(this.printOrderId()!).subscribe({
      next: (order) => {
        this.printOrder.set(order);

        this.previousStatus.set(order.status);
        this.selectedStatus.set(order.status);
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      },
    });
  }

  changeStatus() {}

  statusAsText(status: PrintOrderStatus) {
    switch (status) {
      case PrintOrderStatus.Created:
        return this.translateService.instant('PRINT.ORDER.STATUS.CREATED');
      case PrintOrderStatus.InProgress:
        return this.translateService.instant('PRINT.ORDER.STATUS.IN_PROGRESS');
      case PrintOrderStatus.Completed:
        return this.translateService.instant('PRINT.ORDER.STATUS.COMPLETED');
      case PrintOrderStatus.Canceled:
        return this.translateService.instant('PRINT.ORDER.STATUS.CANCELED');
      case PrintOrderStatus.Collected:
        return this.translateService.instant('PRINT.ORDER.STATUS.COLLECTED');
    }
  }

  paymentStatusAsText(paymentStatus: PaymentStatus) {
    switch (paymentStatus) {
      case PaymentStatus.Paid:
        return this.translateService.instant('PRINT.ORDER.PAYMENT_STATUS.PAID');
      case PaymentStatus.NotPaid:
        return this.translateService.instant('PRINT.ORDER.PAYMENT_STATUS.NOT_PAID');
      case PaymentStatus.Rejected:
        return this.translateService.instant('PRINT.ORDER.PAYMENT_STATUS.REJECTED');
    }
  }

  fileTypeAsText(fileType: FileContentType) {
    switch (fileType) {
      case FileContentType.Pdf:
        return 'PDF';
      case FileContentType.Jpeg:
        return 'JPG';
      case FileContentType.Png:
        return 'PNG';
      case FileContentType.Unknown:
        return 'UNKNOWN';
    }
  }

  getStatusCssClass(): string {
    switch (this.selectedStatus()) {
      case PrintOrderStatus.Created:
        return 'status-created';
      case PrintOrderStatus.InProgress:
        return 'status-in-progress';
      case PrintOrderStatus.Completed:
        return 'status-completed';
      case PrintOrderStatus.Collected:
        return 'status-collected';
      case PrintOrderStatus.Canceled:
        return 'status-canceled';
      default:
        return 'status-created';
    }
  }

  getDownloadFileLink(file: PrintOrderFileDto) {
    this.printOrderService.getFileDownloadUrl(this.printOrder()?.userId!, file.uniqueFileName).subscribe({
      next: (url) => {
        downloadFile(url, file.originalFileName);
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      },
    });
  }

  async downloadFilesInZip() {
    this.loading = await this.loadingCtrl.create({
      message: 'Starting download...',
      backdropDismiss: false,
    });
    await this.loading.present();

    this.blobFilesService
      .downloadInZip(this.printOrder()?.userId!, this.printOrder()?.files.map((f) => f.uniqueFileName)!)
      .subscribe({
        next: (event) => {
          if (event.downloadedBytes !== undefined) {
            this.downloadZipMb.set(+(event.downloadedBytes / (1024 * 1024)).toFixed(2));

            this.loading!.message = `Downloading ${this.downloadZipMb()}MB`;
          }

          if (event.blob) {
            const url = window.URL.createObjectURL(event.blob);

            downloadFile(url, `files_${new Date().toISOString()}.zip`);
            this.loading?.dismiss();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorService.handleError(error);
        },
      });
  }

  async confirmStatusChange() {
    if (this.selectedStatus() === this.previousStatus()) return;

    const text = this.translateService.instant('PRINT.ORDER.STATUS.CHANGE_CONFIRMATION');
    const alert = await this.alertController.create({
      header: this.translateService.instant('CONFIRMATION.DIALOG.TITLE'),
      message: `${text} ${this.statusAsText(this.selectedStatus()!)}?`,
      buttons: [
        {
          text: this.translateService.instant('CONFIRMATION.DIALOG.NO'),
          role: 'cancel',
          handler: () => {
            this.selectedStatus.set(this.previousStatus());
          },
        },
        {
          text: this.translateService.instant('CONFIRMATION.DIALOG.YES'),
          handler: () => {
            this.printOrderService.changeStatus(this.printOrderId()!, this.selectedStatus()!).subscribe({
              next: () => {
                this.loadData();

                this.previousStatus.set(this.selectedStatus());
              },
              error: (error: HttpErrorResponse) => {
                this.errorService.handleError(error);
                this.selectedStatus.set(this.previousStatus());
              },
            });
          },
        },
      ],
    });

    await alert.present();
  }

  private setupTable() {
    this.fileColumns = [
      {
        name: 'PRINT.FILE.ORIGINAL_NAME',
        propertySelector: (file) => file.originalFileName,
      },
      {
        name: 'PRINT.FILE.UNIQUE_NAME',
        propertySelector: (file) => file.uniqueFileName,
      },
      {
        name: 'PRINT.FILE.PAGES',
        propertySelector: (file) => file.pagesQuantity,
      },
      {
        name: 'PRINT.FILE.COPIES',
        propertySelector: (file) => file.copies,
      },
      {
        name: 'PRINT.FILE.TYPE',
        propertySelector: (file) => this.fileTypeAsText(file.fileType),
      },
    ];

    this.fileActions = [
      {
        label: 'PRINT.FILE.DOWNLOAD',
        icon: 'download',
        onClick: (file) => this.getDownloadFileLink(file),
      },
    ];
  }

  private setSelectComponentText() {
    this.selectCompomnentOkText.set(this.translateService.instant('SELECT.COMPONENT.YES'));
    this.selectCompomnentCancelText.set(this.translateService.instant('SELECT.COMPONENT.NO'));

    this.translateService.onLangChange.subscribe(() => {
      this.selectCompomnentOkText.set(this.translateService.instant('SELECT.COMPONENT.YES'));
      this.selectCompomnentCancelText.set(this.translateService.instant('SELECT.COMPONENT.NO'));
    });
  }
}
